import type { IOrganizationProject } from "@frontend-shared/types";
import { useDirection } from "@radix-ui/react-direction";
import { ChevronDown, PlusIcon } from "lucide-react";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";

import { OrganizationAvatar } from "@/components/OrganizationAvatar";
import { ProjectAvatar } from "@/components/ProjectAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import CreateUpdateProjectDialog from "@/pages/settings/organization/details/components/CreateUpdateProjectDialog";

/**
 * NOTE: NSAI-6259 — the project / organization switcher, promoted out of the
 * account menu into the header's top-right corner.
 *
 * NSAI-4345 had made it a `DropdownMenuSub` inside `UserMenu`, where it read as
 * "an option listed in the profile's dropdown" rather than as the control that
 * decides which project everything else on the page belongs to — a switcher
 * nobody could find. It is a first-class header control now, and the corner it
 * moved into is the one the theme picker, the notifications bell and the share
 * button vacated in the same pass. (This is also NSAI-6256, which was filed
 * separately from the same feedback; it is done here, once.)
 *
 * It owns the create-project dialog again. Inside the account menu it could
 * not: the dialog would have unmounted with the dropdown, so `UserMenu` hosted
 * it and this only reported the intent. A sibling of the `DropdownMenu` rather
 * than a child of its content, for exactly that reason.
 */
// NOTE: NSAI-6258 — the personal project has no organization uuid to key its
// section on, so it gets a reserved one. Prefixed to keep it out of any
// possible collision with a real uuid.
const PERSONAL_SECTION_KEY = "section-personal" as const;

// NOTE: a section header reads as a heading and behaves as a disclosure — same
// row height and gutter as the projects under it, so folding one open does not
// shift the column.
const SECTION_HEADER_CLASS =
  "px-4 py-2 rounded-none flex items-center gap-2 text-sm font-semibold border-b cursor-pointer";

const SECTION_CHEVRON_CLASS =
  "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none";

// NOTE: NSAI-6258 — projects sit at the SAME start gutter as the section header
// above them, not indented past it. They used to be pushed in twice over — once
// by the indent that stood in for a mark before they had one, and again by the
// tile itself — which left a visibly empty channel down the left of every row.
// The tile is the hierarchy now, so the indent it replaced is gone.
const PROJECT_ITEM_CLASS = "ps-4 pe-4 rounded-none flex items-center gap-2";

const ProjectMenuComponent = (): JSX.Element | null => {
  const dir = useDirection();
  const { t } = useTranslation("common");
  const { projects, currentProjectUuid, onCurrentProjectChange } = useAuth();
  const [selectedOrganization, setSelectedOrganization] =
    useState<IOrganizationProject | null>(null);
  // NOTE: NSAI-6258 — which sections are unfolded. An account with several
  // organizations made this menu a wall of every project the member has, so a
  // section is a disclosure now: only the one holding the open project starts
  // unfolded, and the rest are one click away. Reset when the menu closes, so
  // it opens on "where I am" rather than on a fold the member has since
  // navigated away from.
  //
  // NOTE: review follow-up — `null` is "nothing toggled yet", NOT an empty
  // array. With the two conflated, folding the one section that starts open
  // produced an empty list, which read as untouched and sprang it open again —
  // so that section could never be closed. An empty array now means exactly
  // what it says: every section explicitly folded.
  const [expandedSectionUuids, setExpandedSectionUuids] = useState<
    string[] | null
  >(null);

  if (!projects) return null;

  const { organizationProjects, personalProject } = projects;

  const personalProjectTitle = personalProject
    ? t("personal_project_title")
    : "";

  // NOTE: NSAI-6258 — the trigger names the project but the badge names the
  // organization it belongs to, which is the compact form of the breadcrumb
  // this control used to spell out: which org, then which project inside it.
  // The personal project belongs to no organization and gets no badge.
  const { currentLabel, currentOrganization } = (() => {
    if (currentProjectUuid && currentProjectUuid !== personalProject?.uuid) {
      for (const organization of organizationProjects || []) {
        const found = organization.projects.find(
          (project) => project.uuid === currentProjectUuid,
        );
        if (found) {
          return {
            currentLabel: found.title,
            currentOrganization: organization,
          };
        }
      }
    }
    return { currentLabel: personalProjectTitle, currentOrganization: null };
  })();

  const currentSectionUuid = currentOrganization?.uuid ?? PERSONAL_SECTION_KEY;

  const handleToggleSection =
    (sectionUuid: string) =>
    (event: Event): void => {
      // NOTE: a header is a disclosure, not a destination — Radix closes the
      // menu on select, and folding a section open only to have the menu
      // vanish under the pointer is the one thing this must not do.
      event.preventDefault();
      setExpandedSectionUuids((currentSections) => {
        // NOTE: the first toggle has to carry the section that was open by
        // default into the list rather than replace it — otherwise unfolding a
        // second organization silently folded away the one the member was
        // already looking at.
        const openSections = currentSections ?? [currentSectionUuid];

        return openSections.includes(sectionUuid)
          ? openSections.filter((uuid) => uuid !== sectionUuid)
          : [...openSections, sectionUuid];
      });
    };

  const isSectionExpanded = (sectionUuid: string): boolean =>
    expandedSectionUuids === null
      ? sectionUuid === currentSectionUuid
      : expandedSectionUuids.includes(sectionUuid);

  return (
    <>
      <DropdownMenu
        dir={dir}
        onOpenChange={(isOpen) => {
          // NOTE: reopening the menu starts from where the member actually is,
          // not from whatever they unfolded last time.
          if (!isOpen) setExpandedSectionUuids(null);
        }}
      >
        <DropdownMenuTrigger asChild>
          {/* NOTE: NSAI-6259 follow-up — the same outline the tour button next
              to it wears: `variant="outline"` at `h-9 rounded-full`. As a bare
              ghost button it read as a floating label rather than as a control,
              which is the half of "promote the switcher" the move alone did not
              deliver. Kept as a plain `button` rather than the `Button`
              component because it needs `group` for the chevron and the outline
              is two utilities. */}
          <button
            type="button"
            id="project-selector"
            data-testid="project-selector"
            title={currentLabel}
            className="group flex h-9 max-w-[12rem] items-center gap-1.5 rounded-full border border-input bg-background px-3.5 text-sm font-medium transition-colors duration-200 ease-out hover:bg-accent hover:text-accent-foreground motion-reduce:transition-none"
          >
            {currentOrganization && (
              <OrganizationAvatar
                name={currentOrganization.name}
                uuid={currentOrganization.uuid}
                className="-ms-2 h-6 w-6 text-[10px]"
              />
            )}
            <span className="min-w-0 truncate">{currentLabel}</span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
              aria-hidden="true"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          data-testid="project-selector-dropdown"
          // NOTE: NSAI-6258 — a row now carries a mark before its name, so the
          // menu is a notch wider: at the old width "Platform Engineering"
          // ellipsised the moment the tile took its 24px.
          className="p-0 w-64"
        >
          {personalProject && (
            <>
              <DropdownMenuItem
                data-testid="project-section-personal"
                aria-expanded={isSectionExpanded(PERSONAL_SECTION_KEY)}
                className={SECTION_HEADER_CLASS}
                onSelect={handleToggleSection(PERSONAL_SECTION_KEY)}
              >
                <span className="min-w-0 flex-1 truncate">
                  {t("personal_project")}
                </span>
                <ChevronDown
                  className={cn(SECTION_CHEVRON_CLASS, {
                    "rotate-180": isSectionExpanded(PERSONAL_SECTION_KEY),
                  })}
                  aria-hidden="true"
                />
              </DropdownMenuItem>
              {isSectionExpanded(PERSONAL_SECTION_KEY) && (
                <DropdownMenuItem
                  data-testid={`project-selector-item-${personalProject.uuid}`}
                  className={cn(
                    PROJECT_ITEM_CLASS,
                    currentProjectUuid === personalProject.uuid &&
                      "bg-accent text-accent-foreground",
                  )}
                  onClick={() => onCurrentProjectChange?.(personalProject.uuid)}
                >
                  <ProjectAvatar name={personalProjectTitle} />
                  <span className="min-w-0 truncate">
                    {personalProjectTitle}
                  </span>
                </DropdownMenuItem>
              )}
            </>
          )}

          {organizationProjects?.map((org) => (
            <div key={org.uuid}>
              <DropdownMenuSeparator className="my-0 border-t border-primary/30" />
              {/* NOTE: NSAI-6258 — the organizations in this list had nothing
                  to tell them apart but their names, which in a customer's
                  account are often variations on one word. The badge is what
                  the eye lands on when the list is scanned rather than read.
                  The row is also the disclosure for the projects under it. */}
              <DropdownMenuItem
                data-testid={`project-section-${org.uuid}`}
                aria-expanded={isSectionExpanded(org.uuid)}
                className={SECTION_HEADER_CLASS}
                onSelect={handleToggleSection(org.uuid)}
              >
                <OrganizationAvatar
                  name={org.name}
                  uuid={org.uuid}
                  className="h-6 w-6 text-[10px]"
                />
                <span className="min-w-0 flex-1 truncate">{org.name}</span>
                <ChevronDown
                  className={cn(SECTION_CHEVRON_CLASS, {
                    "rotate-180": isSectionExpanded(org.uuid),
                  })}
                  aria-hidden="true"
                />
              </DropdownMenuItem>
              {isSectionExpanded(org.uuid) &&
                org.projects.map((p) => (
                  <DropdownMenuItem
                    key={p.uuid}
                    data-testid={`project-selector-item-${p.uuid}`}
                    className={cn(
                      PROJECT_ITEM_CLASS,
                      currentProjectUuid === p.uuid &&
                        "bg-accent text-accent-foreground",
                    )}
                    onClick={() => onCurrentProjectChange?.(p.uuid)}
                  >
                    {/* NOTE: NSAI-6258 — a project's tile takes its
                      organization's hue as a tint, so a column of projects
                      reads as belonging to the organization above it rather
                      than as a second row of badges under it. */}
                    <ProjectAvatar
                      name={p.title}
                      organizationUuid={org.uuid}
                      organizationName={org.name}
                    />
                    <span className="min-w-0 truncate">{p.title}</span>
                  </DropdownMenuItem>
                ))}
              {isSectionExpanded(org.uuid) && org.projects.length === 0 && (
                <DropdownMenuItem
                  data-testid={`project-create-item-${org.uuid}`}
                  className={cn(
                    "ps-4 pe-4 rounded-none flex items-center gap-1 text-primary",
                  )}
                  onClick={() => setSelectedOrganization(org)}
                >
                  <PlusIcon className="w-4 h-4" />
                  {t("create_new_project")}
                </DropdownMenuItem>
              )}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateUpdateProjectDialog
        open={!!selectedOrganization}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelectedOrganization(null);
          }
        }}
        organizationUuid={selectedOrganization?.uuid}
        organizationName={selectedOrganization?.name}
        project={null}
        isUpdateCurrentProject
      />
    </>
  );
};

export const ProjectMenu = memo(ProjectMenuComponent);
